/**
 * Shared hunt list view — read-only hunt list for a shared user
 */

import { useCallback } from 'react';
import { useParams, NavLink, useSearchParams } from 'react-router-dom';
import { Table, Pagination } from '../components/common';
import { useSharedHunts, useSharedWithMe } from '../hooks/useSharing';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { DEFAULT_PAGE_SIZE } from '../utils/constants';
import './SharedCollection.css';
import './SharedHunt.css';

function getColumns() {
  return [
    {
      key: 'distilleries',
      label: 'Distillery',
      sortable: false,
      render: (value) => value?.distillery_name || '-'
    },
    {
      key: 'bottle_name',
      label: 'Name',
      sortable: false
    },
    {
      key: 'type',
      label: 'Type',
      sortable: false,
      render: (value) => value || '-'
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: false,
      render: (value) => {
        const tier = value >= 8 ? 'high' : value >= 4 ? 'medium' : 'low';
        return (
          <span className={`hunt-priority hunt-priority--${tier}`}>
            {value}
          </span>
        );
      }
    },
    {
      key: 'notes',
      label: 'Notes',
      sortable: false,
      render: (value) => value
        ? <span className="hunt-list__notes-cell" title={value}>{value}</span>
        : '-'
    }
  ];
}

function SharedHuntMobileCard({ item }) {
  const tier = item.priority >= 8 ? 'high' : item.priority >= 4 ? 'medium' : 'low';
  return (
    <div className="shared-hunt-card">
      <div className="shared-hunt-card__header">
        <h3 className="shared-hunt-card__name">{item.bottle_name || '—'}</h3>
        <span className={`hunt-priority hunt-priority--${tier}`}>{item.priority}</span>
      </div>
      {item.distilleries && (
        <div className="shared-hunt-card__distillery">{item.distilleries.distillery_name}</div>
      )}
      {item.type && <div className="shared-hunt-card__type">{item.type}</div>}
      {item.notes && <div className="shared-hunt-card__notes">{item.notes}</div>}
    </div>
  );
}

export default function SharedHunt() {
  const { shareId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page')) || 1;
  const pageSize = Number(searchParams.get('pageSize')) || DEFAULT_PAGE_SIZE;

  const updateParams = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === undefined || value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const { data: sharedList = [] } = useSharedWithMe();
  const share = sharedList.find(s => s.share_id === shareId);
  const userId = share?.owner_id;
  const ownerEmail = share?.owner_email || 'Shared User';
  const ownerLabel = share?.owner_display_name || ownerEmail;
  const canViewBottles = share?.share_bottles !== false;
  const canViewHunt = share?.share_hunt !== false;

  const isMobile = useMediaQuery('(max-width: 767px)');

  const { data, isLoading } = useSharedHunts(userId, { page, limit: pageSize });
  const hunts = data?.data || [];
  const totalCount = data?.count || 0;

  const columns = getColumns();

  return (
    <div className="shared-page">
      <div className="shared-page__header">
        <p className="shared-page__viewing-label">
          Viewing <strong title={share?.owner_display_name ? ownerEmail : undefined}>{ownerLabel}</strong>&rsquo;s collection
        </p>
        <div className="shared-page__nav-tabs">
          {canViewBottles && (
            <NavLink
              to={`/shared/${shareId}/dashboard`}
              className={({ isActive }) => `shared-page__nav-tab ${isActive ? 'shared-page__nav-tab--active' : ''}`}
            >
              Dashboard
            </NavLink>
          )}
          {canViewBottles && (
            <NavLink
              to={`/shared/${shareId}/bottles`}
              className={({ isActive }) => `shared-page__nav-tab ${isActive ? 'shared-page__nav-tab--active' : ''}`}
            >
              Bottles
            </NavLink>
          )}
          <NavLink
            to={`/shared/${shareId}/hunt`}
            className={({ isActive }) => `shared-page__nav-tab ${isActive ? 'shared-page__nav-tab--active' : ''}`}
          >
            Hunt List
          </NavLink>
        </div>
      </div>

      <div className="shared-page__subheader">
        <h1 className="shared-page__title">Hunt List</h1>
        {canViewHunt && <span className="shared-page__count">{totalCount} item{totalCount !== 1 ? 's' : ''}</span>}
      </div>

      {!canViewHunt ? (
        <p className="shared-page__locked">The owner has not shared their hunt list with you.</p>
      ) : (
        <>
          {isMobile ? (
            <div className="shared-hunt-card-list">
              {isLoading ? (
                <div className="shared-bottle-card-list__loading">Loading...</div>
              ) : hunts.length === 0 ? (
                <div className="shared-bottle-card-list__empty">No items on this hunt list</div>
              ) : (
                hunts.map(item => (
                  <SharedHuntMobileCard key={item.id} item={item} />
                ))
              )}
            </div>
          ) : (
            <Table
              columns={columns}
              data={hunts}
              loading={isLoading}
              emptyMessage="No items on this hunt list"
            />
          )}

          {totalCount > 0 && (
            <Pagination
              currentPage={page}
              totalItems={totalCount}
              pageSize={pageSize}
              onPageChange={(p) => updateParams({ page: p === 1 ? '' : p })}
              onPageSizeChange={(size) => updateParams({ pageSize: size === DEFAULT_PAGE_SIZE ? '' : size, page: '' })}
            />
          )}
        </>
      )}
    </div>
  );
}
